import cx from 'clsx';
import { format, getTime } from 'date-fns';
import React from 'react';
import ReactDatePicker, { type DatePickerProps } from 'react-datepicker';
import { type Control, type FieldError, useController } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as CalendarIcon } from '@assets/svg/calendar.svg';
import { ReactComponent as InfoIcon } from '@assets/svg/ic-info.svg';

import useTranslation from '@hooks/useTranslation';

import DatepickerHeader from './components/DatepickerHeader';
import style from './style.module.scss';

type IUncontrolledComponentProps = DatePickerProps & {
  label?: string;
  type?: string;
  error?: FieldError;
  isFacilityOffsetAdded?: boolean;
};
type IControlledComponentProps = IUncontrolledComponentProps & {
  control: Control<any>;
  name: string;
  isFacilityOffsetAdded?: boolean;
};

const Component: React.FC<IUncontrolledComponentProps> = ({
  selected,
  onChange,
  error,
  id = 'date-picker-id',
  dateFormat,
  label,
  disabled,
  selectsStart,
  selectsEnd,
  selectsRange,
  selectsMultiple,
  placeholderText,
  popperClassName,
  startDate,
  endDate,
  minDate,
  maxDate,
  filterDate,
  filterTime,
  injectTimes,
  showMonthDropdown,
  showYearDropdown,
  showTimeSelect,
  showTimeSelectOnly,
  timeFormat = 'HH:mm',
  type,
  required,
  isFacilityOffsetAdded,
  onCalendarOpen,
  onCalendarClose,
  timeCaption,
}) => {
  const { t } = useTranslation();

  const handleDatePickerProps = (selectsRange?: boolean, selectsMultiple?: boolean) => {
    if (selectsRange) {
      return {
        selectsRange: selectsRange,
        startDate: startDate,
        endDate: endDate,
        onChange: onChange as (date: [Date | null, Date | null]) => void,
      };
    }
    if (selectsMultiple) {
      return {
        selectsMultiple: selectsMultiple,
        onChange: onChange as (date: Date[] | null) => void,
      };
    }
    return {
      onChange: onChange as (date: Date | null) => void,
    };
  };

  return (
    <div className={style.fieldBlock}>
      {label && (
        <div className={style.labelField}>
          <label className={cx(style.label, { [style.required]: required })} htmlFor={id}>
            {label}
          </label>
          {isFacilityOffsetAdded && <InfoIcon id="info-icon" className={style.infoIcon} />}
        </div>
      )}
      <div className={style.datepickerWrapper}>
        <ReactDatePicker
          id={id}
          selected={selected}
          dateFormat={dateFormat}
          disabled={disabled}
          showPopperArrow={false}
          className={cx(style.datepicker, {
            [style.datepickerFilled]: !!startDate || !!selected,
          })}
          calendarClassName={cx({
            [style.datePickerWithTime]: showTimeSelect,
            [style.injectedTimes]: injectTimes,
          })}
          timeCaption={timeCaption || format(getTime(new Date()), 'HH:mm')}
          renderCustomHeader={(props) => {
            return (
              <DatepickerHeader
                showMonthDropdown={showMonthDropdown}
                showYearDropdown={showYearDropdown}
                minDate={minDate}
                maxDate={maxDate}
                {...props}
              />
            );
          }}
          autoComplete="off"
          selectsStart={selectsStart}
          selectsEnd={selectsEnd}
          placeholderText={placeholderText}
          minDate={minDate}
          maxDate={maxDate}
          filterDate={filterDate}
          monthsShown={1}
          filterTime={filterTime}
          injectTimes={injectTimes}
          showTimeSelect={showTimeSelect}
          showTimeSelectOnly={showTimeSelectOnly}
          timeFormat={timeFormat}
          popperClassName={popperClassName}
          onCalendarOpen={onCalendarOpen}
          onCalendarClose={onCalendarClose}
          formatWeekDay={(day) => day.charAt(0)}
          icon={<CalendarIcon className={style.calendarIcon} />}
          showIcon
          {...handleDatePickerProps(selectsRange, selectsMultiple)}
        />
      </div>
      {type !== 'woHelperText' && <div className={style.helperText}>{error && t(error?.message)}</div>}
      <Tooltip anchorSelect="#info-icon" content={t('Original time based on facility time zone')} />
    </div>
  );
};

const Controlled: React.FC<IControlledComponentProps> = ({
  control,
  name,
  dateFormat,
  label,
  disabled,
  selectsStart,
  selectsEnd,
  selectsRange,
  placeholderText,
  startDate,
  selectsMultiple,
  popperClassName,
  endDate,
  minDate,
  maxDate,
  filterDate,
  filterTime,
  injectTimes,
  showMonthDropdown,
  showYearDropdown,
  showTimeSelect,
  showTimeSelectOnly,
  timeFormat,
  type,
  required,
  isFacilityOffsetAdded,
  timeCaption,
}) => {
  const {
    field: { value, onChange },
    fieldState: { error },
  } = useController({
    control,
    name,
    disabled,
  });

  const handleDatePickerProps = (selectsRange?: boolean, selectsMultiple?: boolean) => {
    if (selectsRange) {
      return {
        selectsRange: selectsRange,
        startDate: startDate || value[0],
        endDate: endDate || value[1],
        onChange: onChange as (date: [Date | null, Date | null]) => void,
      };
    }
    if (selectsMultiple) {
      return {
        selectsMultiple: selectsMultiple,
        onChange: onChange as (date: Date[] | null) => void,
        selected: value,
      };
    }
    return {
      onChange: onChange as (date: Date | null) => void,
      selected: value,
    };
  };

  return (
    <Component
      error={error}
      id={name}
      dateFormat={dateFormat}
      label={label}
      disabled={disabled}
      selectsStart={selectsStart}
      selectsEnd={selectsEnd}
      placeholderText={placeholderText}
      popperClassName={popperClassName}
      minDate={minDate}
      maxDate={maxDate}
      filterDate={filterDate}
      filterTime={filterTime}
      injectTimes={injectTimes}
      showMonthDropdown={showMonthDropdown}
      showYearDropdown={showYearDropdown}
      showTimeSelect={showTimeSelect}
      showTimeSelectOnly={showTimeSelectOnly}
      timeFormat={timeFormat}
      type={type}
      isFacilityOffsetAdded={isFacilityOffsetAdded}
      required={required}
      timeCaption={timeCaption}
      {...handleDatePickerProps(selectsRange, selectsMultiple)}
    />
  );
};

const DatePicker = (props: IUncontrolledComponentProps | IControlledComponentProps) => {
  if ('control' in props) {
    return <Controlled {...props} />;
  }

  return <Component {...props} />;
};

export default DatePicker;
